import React, {
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from 'react';
import type { KeyboardEvent } from 'react';
import { graphql } from 'gatsby';
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import * as styles from './content-explorer.module.css';
import {
    KontentRichText,
    BrandColor,
    KontentRadioMultiChoice,
} from '../../../types/KontentProps';
import RichText from '../Global/RichText';
import { getColor, getMultiChoice } from '../Global/DataUtils';
import { useIsClient } from '../Global/DataUtils/useIsClient';
import { isRichTextFieldBlank } from '../../global/utility';
import {
    AUTO_ADVANCE_MS,
    DEFAULT_GRADIENT_END,
    DEFAULT_GRADIENT_START,
    normalizeContentExplorerSlides,
    type NormalizedContentExplorerSlide,
} from './contentExplorerUtils';
import { ContentExplorerTabs } from './ContentExplorerTabs';
import { ContentExplorerMobileAccordion } from './ContentExplorerMobileAccordion';
import { ContentExplorerPanel } from './ContentExplorerPanel';

type YesNo = 'yes' | 'no';

export interface ContentExplorerProps {
    kontentItemId?: string;
    headline?: KontentRichText;
    description?: KontentRichText;
    bottom_copy?: KontentRichText;
    gradient_background?: KontentRadioMultiChoice<YesNo>;
    background_color_1?: BrandColor;
    background_color_2?: BrandColor;
    font_color?: BrandColor;
    content_explorer_slide?: {
        value?: unknown;
    };
    auto_scroll?: KontentRadioMultiChoice<YesNo>;
    content_explorer_background_color?: BrandColor;
    content_explorer_primary_font_color?: BrandColor;
    content_explorer_secondary_font_color?: BrandColor;
    anchor?: { value?: string };
}

const ContentExplorer: React.FC<ContentExplorerProps> = ({
    kontentItemId,
    headline,
    description,
    bottom_copy,
    gradient_background,
    background_color_1,
    background_color_2,
    font_color,
    content_explorer_slide,
    auto_scroll,
    content_explorer_background_color,
    content_explorer_primary_font_color,
    content_explorer_secondary_font_color,
    anchor,
}) => {
    const breakpoints = useBreakpoint();
    const baseId = useId().replace(/:/g, '');
    const panelId = `${baseId}-explorer-panel`;

    const slides = useMemo(
        () =>
            normalizeContentExplorerSlides(
                content_explorer_slide as Parameters<
                    typeof normalizeContentExplorerSlides
                >[0]
            ),
        [content_explorer_slide]
    );

    const [activeIndex, setActiveIndex] = useState(0);
    const [mobileAccordionExpanded, setMobileAccordionExpanded] =
        useState(true);
    const [mobileAccordionClosingIndex, setMobileAccordionClosingIndex] =
        useState<number | null>(null);
    const [userPausedInteraction, setUserPausedInteraction] = useState(false);
    const [videoModalOpen, setVideoModalOpen] = useState(false);
    const [sectionInView, setSectionInView] = useState(true);
    const sectionRef = useRef<HTMLElement>(null);

    const slideCount = slides.length;

    useEffect(() => {
        if (activeIndex >= slideCount && slideCount > 0) {
            setActiveIndex(0);
        }
    }, [activeIndex, slideCount]);

    const autoScrollOn =
        (getMultiChoice(auto_scroll) || '').toLowerCase() === 'yes';
    const gradientOn =
        (getMultiChoice(gradient_background) || '').toLowerCase() === 'yes';

    const bg1 = getColor(background_color_1) || DEFAULT_GRADIENT_START;
    const bg2 = getColor(background_color_2) || DEFAULT_GRADIENT_END;
    const sectionTextColor = getColor(font_color) || '#ffffff';

    const sectionBackground = useMemo(() => {
        if (gradientOn) {
            return `linear-gradient(180deg, ${bg1} 0%, ${bg2} 100%)`;
        }
        return bg1;
    }, [gradientOn, bg1, bg2]);

    const panelBg = getColor(content_explorer_background_color) || '#1a1816';
    const panelPrimary =
        getColor(content_explorer_primary_font_color) || '#ffffff';
    const panelSecondary =
        getColor(content_explorer_secondary_font_color) || '#f06623';

    const isLargeViewport = breakpoints.xl;
    const isClient = useIsClient();

    useEffect(() => {
        if (isLargeViewport) {
            setMobileAccordionClosingIndex(null);
        }
    }, [isLargeViewport]);

    useEffect(() => {
        const el = sectionRef.current;
        if (!el || typeof IntersectionObserver === 'undefined') {
            return undefined;
        }
        const observer = new IntersectionObserver(
            ([entry]) => {
                setSectionInView(entry.isIntersecting);
            },
            { threshold: 0.15 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!autoScrollOn || slideCount < 2) return undefined;
        if (userPausedInteraction) return undefined;
        if (videoModalOpen) return undefined;
        if (!sectionInView) return undefined;
        if (!isLargeViewport) return undefined; // Disable auto scroll on mobile (accordion view)

        const id = window.setInterval(() => {
            setActiveIndex((i) => (i + 1) % slideCount);
        }, AUTO_ADVANCE_MS);
        return () => window.clearInterval(id);
    }, [
        autoScrollOn,
        slideCount,
        userPausedInteraction,
        videoModalOpen,
        sectionInView,
        isLargeViewport,
    ]);

    const onVideoOpenChange = useCallback((open: boolean) => {
        setVideoModalOpen(open);
    }, []);

    const selectTab = useCallback((index: number) => {
        setUserPausedInteraction(true);
        setActiveIndex(index);
    }, []);

    const onMobileAccordionRowClick = useCallback(
        (index: number) => {
            if (mobileAccordionExpanded && activeIndex === index) {
                setMobileAccordionClosingIndex(index);
                setMobileAccordionExpanded(false);
                return;
            }
            setMobileAccordionClosingIndex(null);
            setUserPausedInteraction(true);
            setActiveIndex(index);
            setMobileAccordionExpanded(true);
        },
        [mobileAccordionExpanded, activeIndex]
    );

    const onMobileAccordionCloseDone = useCallback(() => {
        setMobileAccordionClosingIndex(null);
    }, []);

    const focusTab = useCallback(
        (index: number) => {
            window.requestAnimationFrame(() => {
                document
                    .getElementById(`${baseId}-explorer-tab-${index}`)
                    ?.focus();
            });
        },
        [baseId]
    );

    const activateTab = useCallback(
        (index: number) => {
            setUserPausedInteraction(true);
            setActiveIndex(index);
            focusTab(index);
        },
        [focusTab]
    );

    const onTabListKeyDown = useCallback(
        (e: KeyboardEvent<HTMLDivElement>) => {
            if (slideCount < 2) return;
            if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
                e.preventDefault();
                activateTab((activeIndex + 1) % slideCount);
            } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
                e.preventDefault();
                activateTab((activeIndex - 1 + slideCount) % slideCount);
            } else if (e.key === 'Home') {
                e.preventDefault();
                activateTab(0);
            } else if (e.key === 'End') {
                e.preventDefault();
                activateTab(slideCount - 1);
            }
        },
        [slideCount, activeIndex, activateTab]
    );

    const currentSlide: NormalizedContentExplorerSlide | undefined =
        slides[activeIndex];

    if (!slideCount || !currentSlide) {
        return null;
    }

    const panelAriaLabelledBy =
        slideCount >= 2 && !isLargeViewport && mobileAccordionExpanded
            ? `${baseId}-mobile-accordion-${activeIndex}`
            : `${baseId}-explorer-tab-${activeIndex}`;

    const sectionId = anchor?.value || undefined;

    const explorerPanel = (
        <ContentExplorerPanel
            styles={styles}
            currentSlide={currentSlide}
            activeIndex={activeIndex}
            panelId={panelId}
            panelBg={panelBg}
            panelPrimary={panelPrimary}
            panelSecondary={panelSecondary}
            ariaLabelledBy={panelAriaLabelledBy}
            onVideoOpenChange={onVideoOpenChange}
        />
    );

    return (
        <section
            ref={sectionRef}
            className={styles.section}
            id={sectionId}
            data-kontent-item-id={kontentItemId}
            style={
                {
                    background: sectionBackground,
                    color: sectionTextColor,
                    '--ce-bg-start': bg1,
                    '--ce-bg-end': gradientOn ? bg2 : bg1,
                } as React.CSSProperties
            }
        >
            <div className={styles.inner}>
                <div className={styles.topRow}>
                    {headline && !isRichTextFieldBlank(headline.value) ? (
                        <div className={styles.sectionHeadlineOuter}>
                            <div
                                className={styles.sectionHeadline}
                                style={{ color: sectionTextColor }}
                            >
                                {RichText(
                                    headline,
                                    false,
                                    undefined,
                                    undefined,
                                    'headline'
                                )}
                            </div>
                        </div>
                    ) : null}
                    {description && !isRichTextFieldBlank(description.value) ? (
                        <div
                            className={styles.topDescription}
                            style={{ color: sectionTextColor }}
                        >
                            {RichText(
                                description,
                                false,
                                undefined,
                                undefined,
                                'description'
                            )}
                        </div>
                    ) : null}
                </div>

                <div className={styles.explorerRow}>
                    {(!isClient || isLargeViewport) && (
                        <ContentExplorerTabs
                            slides={slides}
                            activeIndex={activeIndex}
                            baseId={baseId}
                            panelId={panelId}
                            styles={styles}
                            selectTab={selectTab}
                            onTabListKeyDown={onTabListKeyDown}
                            setUserPausedInteraction={setUserPausedInteraction}
                        />
                    )}

                    {slideCount >= 2 && isLargeViewport ? (
                        <div className={styles.slideChrome}>
                            <div className={styles.slideChromeMain}>
                                {explorerPanel}
                            </div>
                        </div>
                    ) : null}

                    {slideCount >= 2 && !isLargeViewport ? (
                        <ContentExplorerMobileAccordion
                            slides={slides}
                            activeIndex={activeIndex}
                            panelExpanded={mobileAccordionExpanded}
                            closeAnimatingIndex={mobileAccordionClosingIndex}
                            onCloseAnimationComplete={
                                onMobileAccordionCloseDone
                            }
                            baseId={baseId}
                            styles={styles}
                            onRowClick={onMobileAccordionRowClick}
                            panelId={panelId}
                        >
                            {explorerPanel}
                        </ContentExplorerMobileAccordion>
                    ) : null}

                    {slideCount < 2 ? explorerPanel : null}
                </div>

                {bottom_copy && !isRichTextFieldBlank(bottom_copy.value) ? (
                    <div
                        className={styles.bottomCopy}
                        style={{ color: sectionTextColor }}
                    >
                        {RichText(
                            bottom_copy,
                            false,
                            undefined,
                            undefined,
                            'bottom_copy'
                        )}
                    </div>
                ) : null}
            </div>
        </section>
    );
};

export default ContentExplorer;

export const fragmentContentExplorer = graphql`
    fragment ContentExplorer on kontent_item_component___content_explorer {
        elements {
            headline {
                ...kontentRichText
            }
            description {
                ...kontentRichText
            }
            bottom_copy {
                ...kontentRichText
            }
            anchor {
                value
            }
            auto_scroll {
                value {
                    codename
                }
            }
            gradient_background {
                value {
                    codename
                }
            }
            background_color_1 {
                ...kontentColor
            }
            background_color_2 {
                ...kontentColor
            }
            font_color {
                ...kontentColor
            }
            content_explorer_background_color {
                ...kontentColor
            }
            content_explorer_primary_font_color {
                ...kontentColor
            }
            content_explorer_secondary_font_color {
                ...kontentColor
            }
            content_explorer_slide {
                value {
                    ... on kontent_item_content_item___content_explorer_slide {
                        id
                        elements {
                            headline {
                                ...kontentRichText
                            }
                            sub_headline {
                                ...kontentRichText
                            }
                            description {
                                ...kontentRichText
                            }
                            highlight_value {
                                ...kontentRichText
                            }
                            supporting_context {
                                ...kontentRichText
                            }
                            context_label {
                                ...kontentRichText
                            }
                            image {
                                ...kontentImage
                            }
                            categories {
                                value {
                                    ...RteComponent
                                }
                            }
                            cta_configuration__cta_label {
                                value
                            }
                            cta_configuration__cta_location_anchor_point {
                                value
                            }
                            cta_configuration__cta_location {
                                ...kontentCtaLocation
                            }
                            cta_configuration__cta_window_action {
                                value {
                                    codename
                                    name
                                }
                            }
                            cta_type {
                                value {
                                    codename
                                }
                            }
                            video_link {
                                value
                            }
                            play_button_accessibility_label {
                                value
                            }
                        }
                    }
                }
            }
        }
    }
`;
